// src/components/admin/settings/NavbarSettings.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaUpload, FaTrash, FaShoppingCart, FaSearch, FaBars } from 'react-icons/fa';
import './NavbarSettings.css';

const NavbarSettings = () => {
  const [settings, setSettings] = useState({
    // Logo
    logo: '',
    logo_width: '45',

    // Navbar background + items color/size
    navbar_bg_color: '#ffffff',
    navbar_text_color: '#333333',
    navbar_font_size: '14',

    // Icons
    search_icon_enabled: true,
    search_icon_size: '18',
    search_icon_color: '#333333',

    cart_icon_enabled: true,
    cart_icon_size: '18',
    cart_icon_color: '#333333',
    cart_badge_bg: '#667eea',
    cart_badge_color: '#ffffff',

    // Mobile menu
    mobile_menu_bg: '#ffffff',
    mobile_menu_text_color: '#333333',
    mobile_menu_hover_color: '#667eea',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const limits = {
    logo_width: { min: 20, max: 200, label: 'Logo Width' },
    navbar_font_size: { min: 10, max: 24, label: 'Font Size' },
    search_icon_size: { min: 14, max: 30, label: 'Search Icon Size' },
    cart_icon_size: { min: 14, max: 30, label: 'Cart Icon Size' },
  };

  const validateValue = (name, value) => {
    const limit = limits[name];
    if (limit) {
      const numValue = parseInt(value);
      if (numValue < limit.min) {
        toast.warning(`${limit.label} cannot be less than ${limit.min}px. Setting to minimum.`);
        return limit.min;
      }
      if (numValue > limit.max) {
        toast.warning(`${limit.label} cannot exceed ${limit.max}px. Setting to maximum.`);
        return limit.max;
      }
    }
    return value;
  };

  const loadSettings = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load navbar settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size should be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    setUploadingLogo(true);

    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/upload-logo.php', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setSettings({ ...settings, logo: data.image_url });
        toast.success('Logo uploaded successfully!');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleChange = (e) => {
    let value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    if (e.target.type === 'number') {
      value = validateValue(e.target.name, value);
    }

    setSettings({ ...settings, [e.target.name]: value });

    if (e.target.type === 'color') {
      toast.info(`${e.target.name.replace(/_/g, ' ')} changed to ${value}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logo: settings.logo,
          logo_width: settings.logo_width,
          // TODO: explicit empty value meant as an "auto height" signal to
          // the live storefront's Navbar component — I haven't seen that
          // file, so can't confirm it actually treats an empty
          // logo_height as auto rather than something unexpected. Worth
          // verifying once this is live.
          logo_height: '',

          navbar_bg_color: settings.navbar_bg_color,
          navbar_text_color: settings.navbar_text_color,
          navbar_font_size: settings.navbar_font_size,

          search_icon_enabled: settings.search_icon_enabled,
          search_icon_size: settings.search_icon_size,
          search_icon_color: settings.search_icon_color,

          cart_icon_enabled: settings.cart_icon_enabled,
          cart_icon_size: settings.cart_icon_size,
          cart_icon_color: settings.cart_icon_color,
          cart_badge_bg: settings.cart_badge_bg,
          cart_badge_color: settings.cart_badge_color,

          mobile_menu_bg: settings.mobile_menu_bg,
          mobile_menu_text_color: settings.mobile_menu_text_color,
          mobile_menu_hover_color: settings.mobile_menu_hover_color,
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Navbar settings saved successfully!');
        window.dispatchEvent(new Event('settingsUpdated'));
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loader">Loading navbar settings...</div>;

  return (
    <div className="navbar-settings-page">
      <h2>Navbar Settings</h2>
      <p className="section-description">Customize your navigation bar appearance and behavior</p>

      {/* Desktop Live Preview */}
      <div className="navbar-preview-section">
        <h3>Live Preview — Desktop</h3>
        <div className="navbar-preview" style={{ backgroundColor: settings.navbar_bg_color }}>
          <div className="preview-container">
            <div className="preview-logo">
              {settings.logo ? (
                <img
                  src={settings.logo}
                  alt="Logo"
                  style={{ width: `${settings.logo_width}px`, height: 'auto' }}
                />
              ) : (
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>LOGO</span>
              )}
            </div>
            <div className="preview-links">
              {['Home', 'Shop', 'About', 'Contact'].map((label) => (
                <span
                  key={label}
                  style={{ color: settings.navbar_text_color, fontSize: `${settings.navbar_font_size}px` }}
                >
                  {label}
                </span>
              ))}
            </div>
            <div className="preview-icons">
              {settings.search_icon_enabled && <FaSearch size={parseInt(settings.search_icon_size)} color={settings.search_icon_color} />}
              {settings.cart_icon_enabled && (
                <span className="preview-cart-wrapper">
                  <FaShoppingCart size={parseInt(settings.cart_icon_size)} color={settings.cart_icon_color} />
                  <span
                    className="preview-cart-badge"
                    style={{ background: settings.cart_badge_bg, color: settings.cart_badge_color }}
                  >
                    2
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Live Preview */}
{/*       
      <div className="navbar-preview-section">
        <h3>Live Preview — Mobile Menu</h3>
        <div className="mobile-preview-frame">
          <div className="mobile-preview-header">
            <FaBars size={16} />
            <span>Menu</span>
          </div>
          <div className="mobile-preview-menu" style={{ background: settings.mobile_menu_bg }}>
            {['Home', 'Sale', 'About', 'Contact'].map((label, i) => (
              <div
                key={label}
                className="mobile-preview-item"
                style={{
                  color: i === 1 ? settings.mobile_menu_hover_color : settings.mobile_menu_text_color
                }}
              >
                {label}{i === 1 ? ' (hover example)' : ''}
              </div>
            ))}
          </div>
        </div>
      </div>
*/}      

      <form onSubmit={handleSubmit} className="navbar-settings-form">
        {/* Logo */}
        <div className="settings-group">
          <h3>Logo</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Store Logo</label>
              <div className="logo-upload-area">
                {settings.logo && (
                  <div className="logo-preview">
                    <img src={settings.logo} alt="Logo" />
                    <button type="button" className="remove-logo-btn" onClick={() => setSettings({ ...settings, logo: '' })}>
                      <FaTrash /> Remove
                    </button>
                  </div>
                )}
                <input type="file" id="logoUpload" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                <button type="button" className="upload-btn" onClick={() => document.getElementById('logoUpload').click()} disabled={uploadingLogo}>
                  <FaUpload /> {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </button>
                <small className="field-hint">Max file size: 2MB</small>
              </div>
            </div>
            <div className="form-group">
              <label>Logo Width - px</label>
              <input type="number" name="logo_width" value={settings.logo_width} onChange={handleChange} min="20" max="200" step="1" />
              <small className="field-hint">Height adjusts automatically to keep the logo's proportions. Min: 20px, Max: 200px</small>
            </div>
          </div>
        </div>

        {/* Navbar Colors */}
        <div className="settings-group">
          <h3>Navbar Background &amp; Items Color</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Navbar Background Color</label>
              <div className="color-input-wrapper">
                <input type="color" name="navbar_bg_color" value={settings.navbar_bg_color} onChange={handleChange} />
                <span className="color-hex">{settings.navbar_bg_color}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Navbar Items Color</label>
              <div className="color-input-wrapper">
                <input type="color" name="navbar_text_color" value={settings.navbar_text_color} onChange={handleChange} />
                <span className="color-hex">{settings.navbar_text_color}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Navbar Items Font Size - px</label>
              <input type="number" name="navbar_font_size" value={settings.navbar_font_size} onChange={handleChange} min="10" max="24" step="1" />
              <small className="field-hint">Min: 10px, Max: 24px</small>
            </div>
          </div>
        </div>

        {/* Icons */}
        <div className="settings-group">
          <h3>Icon Settings</h3>

          <h4>Search Icon</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" name="search_icon_enabled" checked={settings.search_icon_enabled} onChange={handleChange} />
                Enable Search Icon
              </label>
            </div>
            <div className="form-group">
              <label>Icon Size - px</label>
              <input type="number" name="search_icon_size" value={settings.search_icon_size} onChange={handleChange} min="14" max="30" />
              <small className="field-hint">Min: 14px, Max: 30px</small>
            </div>
            <div className="form-group">
              <label>Icon Color</label>
              <div className="color-input-wrapper">
                <input type="color" name="search_icon_color" value={settings.search_icon_color} onChange={handleChange} />
                <span className="color-hex">{settings.search_icon_color}</span>
              </div>
            </div>
          </div>

          <h4>Cart Icon</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" name="cart_icon_enabled" checked={settings.cart_icon_enabled} onChange={handleChange} />
                Enable Cart Icon
              </label>
            </div>
            <div className="form-group">
              <label>Icon Size - px</label>
              <input type="number" name="cart_icon_size" value={settings.cart_icon_size} onChange={handleChange} min="14" max="30" />
              <small className="field-hint">Min: 14px, Max: 30px</small>
            </div>
            <div className="form-group">
              <label>Icon Color</label>
              <div className="color-input-wrapper">
                <input type="color" name="cart_icon_color" value={settings.cart_icon_color} onChange={handleChange} />
                <span className="color-hex">{settings.cart_icon_color}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Cart Badge Background</label>
              <div className="color-input-wrapper">
                <input type="color" name="cart_badge_bg" value={settings.cart_badge_bg} onChange={handleChange} />
                <span className="color-hex">{settings.cart_badge_bg}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Cart Badge Text Color</label>
              <div className="color-input-wrapper">
                <input type="color" name="cart_badge_color" value={settings.cart_badge_color} onChange={handleChange} />
                <span className="color-hex">{settings.cart_badge_color}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className="settings-group">
          <h3>Mobile Menu Settings</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Mobile Menu Background</label>
              <div className="color-input-wrapper">
                <input type="color" name="mobile_menu_bg" value={settings.mobile_menu_bg} onChange={handleChange} />
                <span className="color-hex">{settings.mobile_menu_bg}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Mobile Menu Text Color</label>
              <div className="color-input-wrapper">
                <input type="color" name="mobile_menu_text_color" value={settings.mobile_menu_text_color} onChange={handleChange} />
                <span className="color-hex">{settings.mobile_menu_text_color}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Mobile Menu Hover Color</label>
              <div className="color-input-wrapper">
                <input type="color" name="mobile_menu_hover_color" value={settings.mobile_menu_hover_color} onChange={handleChange} />
                <span className="color-hex">{settings.mobile_menu_hover_color}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="form-buttons">
          <button type="submit" disabled={saving} className="save-btn">
            {saving ? 'Saving...' : 'Save Navbar Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NavbarSettings;